import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import './ImportModal.css';

/**
 * Import Modal Component
 * Two tabs: Paste Text (old design) + Upload Excel (new)
 * Paste Text tab matches old WebForms StudentUniform2.aspx design
 * Excel parsing done in frontend using SheetJS library
 */
const ImportModal = ({ isOpen, onClose, onDataParsed, sizeConfig }) => {
  const { t } = useTranslation(['studentUniform', 'common']);
  const [activeTab, setActiveTab] = useState('paste'); // 'paste' or 'upload'
  const [pasteData, setPasteData] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [worksheets, setWorksheets] = useState([]); // List of worksheet names
  const [selectedWorksheet, setSelectedWorksheet] = useState(''); // Selected worksheet
  const [parsedExcelData, setParsedExcelData] = useState(null); // Parsed students from Excel
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  // Handle paste data change
  const handlePasteChange = (e) => {
    setPasteData(e.target.value);
    setError(null);
  };

  // Handle file selection - parse Excel immediately to detect worksheets
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      setError(t('studentUniform:importModal.errors.invalidFileType'));
      setSelectedFile(null);
      setWorksheets([]);
      setSelectedWorksheet('');
      setParsedExcelData(null);
      return;
    }

    setSelectedFile(file);
    setError(null);
    setIsParsing(true);

    try {
      // Parse Excel to get worksheets
      const result = await parseExcelFile(file);

      setWorksheets(result.worksheets);
      setParsedExcelData(result.students);

      // If multiple worksheets, prompt user to select
      if (result.worksheets.length > 1) {
        console.log('ImportModal: Multiple worksheets detected, user must select one');
        // Default to first worksheet or STUDENT sheet
        const defaultSheet = result.worksheets.find(name =>
          name.toUpperCase().includes('STUDENT')
        ) || result.worksheets[0];
        setSelectedWorksheet(defaultSheet);
      } else {
        setSelectedWorksheet(result.worksheets[0]);
      }

    } catch (err) {
      console.error('ImportModal: Error parsing Excel file:', err);
      setError(err.message || 'Failed to parse Excel file');
      setSelectedFile(null);
      setWorksheets([]);
      setSelectedWorksheet('');
      setParsedExcelData(null);
    } finally {
      setIsParsing(false);
    }
  };

  // Parse tab-delimited data (13 columns fixed order)
  const parseTabDelimitedData = (text) => {
    const rows = text.split('\n').map(row => row.trim()).filter(row => row !== '');

    if (rows.length === 0) {
      throw new Error('No data found. Please paste student data from Excel.');
    }

    const students = [];
    const expectedColumnCount = 13;

    // Expected column order (FIXED - matching old WebForms)
    const columnOrder = [
      'NAMA', 'NOKP', 'STATUSWARGANEGARA', 'TAHUN', 'KELAS', 'JANTINA',
      'BAJU PUTIH', 'SELUAR PANJANG', 'SELUAR PENDEK',
      'BAJU KURUNG', 'KAIN KURUNG', 'PINAFORE', 'KASUT'
    ];

    // Check if first row is header (optional)
    let startIndex = 0;
    const firstRowCols = rows[0].split('\t').map(col => col.trim().toUpperCase());
    const isHeader = (
      (firstRowCols[0] === 'NAME' || firstRowCols[0] === 'NAMA') &&
      (firstRowCols[1] === 'IC' || firstRowCols[1] === 'NOKP')
    );

    if (isHeader) {
      console.log('ImportModal: Header row detected, skipping first row');
      startIndex = 1;
    }

    // Parse data rows
    for (let i = startIndex; i < rows.length; i++) {
      const cols = rows[i].split('\t').map(col => col.trim());

      // Validate column count
      if (cols.length !== expectedColumnCount) {
        throw new Error(
          `Row ${i + 1}: Expected ${expectedColumnCount} columns, got ${cols.length}.\n` +
          `Please ensure data includes all columns in this order:\n${columnOrder.join(' | ')}`
        );
      }

      // Skip empty rows
      if (cols.every(col => col === '')) {
        continue;
      }

      // Parse student data
      const student = {
        name: cols[0] || '',
        noKP: (cols[1] || '').replace(/^'/, ''), // Remove leading apostrophe
        citizenStatus: cols[2] || '',
        tahun: cols[3] || '',
        class: cols[4] || '',
        jantina: cols[5] || '',
        bajuPutih: cols[6] || '',
        seluarPanjang: cols[7] || '',
        seluarPendek: cols[8] || '',
        bajuKurung: cols[9] || '',
        kainKurung: cols[10] || '',
        pinafore: cols[11] || '',
        kasut: cols[12] || '',
        sourceRow: i + 1, // Track row number for error messages
      };

      students.push(student);
    }

    console.log(`ImportModal: Parsed ${students.length} students from pasted data`);
    return students;
  };

  // Parse Excel file using SheetJS
  const parseExcelFile = (file, worksheetName) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          console.log('ImportModal: Reading Excel file');
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          console.log(`ImportModal: Found ${workbook.SheetNames.length} worksheets:`, workbook.SheetNames);

          // If worksheetName not specified, find "STUDENT" sheet or use first sheet
          let sheetName = worksheetName;
          if (!sheetName) {
            // Try to find STUDENT sheet (case-insensitive)
            const studentSheet = workbook.SheetNames.find(name =>
              name.toUpperCase().includes('STUDENT')
            );
            sheetName = studentSheet || workbook.SheetNames[0];
            console.log(`ImportModal: Using worksheet: ${sheetName}`);
          }

          const worksheet = workbook.Sheets[sheetName];
          if (!worksheet) {
            throw new Error(`Worksheet "${sheetName}" not found`);
          }

          // Convert to JSON with headers
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          console.log(`ImportModal: Worksheet has ${jsonData.length} rows`);

          if (jsonData.length < 2) {
            throw new Error('Excel file must have at least a header row and one data row');
          }

          // Parse header row and create column map (flexible header matching)
          const headerRow = jsonData[0];
          const columnMap = {};

          headerRow.forEach((header, index) => {
            const headerUpper = String(header || '').toUpperCase().trim();

            // Flexible column mapping (like old ExcelParser.cs)
            if (headerUpper.includes('NAMA') || headerUpper === 'NAME') {
              columnMap['NAMA'] = index;
            } else if (headerUpper.includes('NOKP') || headerUpper.includes('NO KP') || headerUpper.includes('NO. KP') || headerUpper === 'IC') {
              columnMap['NOKP'] = index;
            } else if (headerUpper.includes('STATUS') && headerUpper.includes('WARGA')) {
              columnMap['STATUSWARGANEGARA'] = index;
            } else if (headerUpper === 'TAHUN' || headerUpper === 'GRADE') {
              columnMap['TAHUN'] = index;
            } else if (headerUpper.includes('KELAS') || headerUpper === 'CLASS') {
              columnMap['KELAS'] = index;
            } else if (headerUpper.includes('JANTINA') || headerUpper === 'GENDER') {
              columnMap['JANTINA'] = index;
            } else if (headerUpper.includes('BAJU') && headerUpper.includes('PUTIH')) {
              columnMap['BAJU PUTIH'] = index;
            } else if (headerUpper.includes('SELUAR') && headerUpper.includes('PANJANG')) {
              columnMap['SELUAR PANJANG'] = index;
            } else if (headerUpper.includes('SELUAR') && headerUpper.includes('PENDEK')) {
              columnMap['SELUAR PENDEK'] = index;
            } else if (headerUpper.includes('BAJU') && headerUpper.includes('KURUNG')) {
              columnMap['BAJU KURUNG'] = index;
            } else if (headerUpper.includes('KAIN') && headerUpper.includes('KURUNG')) {
              columnMap['KAIN KURUNG'] = index;
            } else if (headerUpper.includes('PINA')) {
              columnMap['PINAFORE'] = index;
            } else if (headerUpper.includes('KASUT') || headerUpper === 'SHOE') {
              columnMap['KASUT'] = index;
            }
          });

          console.log('ImportModal: Column mapping:', columnMap);

          // Validate required columns
          const requiredColumns = ['NAMA', 'NOKP', 'JANTINA'];
          const missingColumns = requiredColumns.filter(col => columnMap[col] === undefined);

          if (missingColumns.length > 0) {
            throw new Error(
              `Missing required columns: ${missingColumns.join(', ')}\n\n` +
              `Headers found: ${headerRow.join(', ')}\n\n` +
              `Required: NAMA (or Name), NOKP (or IC, NO KP), JANTINA (or Gender)`
            );
          }

          // Parse data rows (skip header)
          const students = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];

            // Get values from mapped columns
            const nama = String(row[columnMap['NAMA']] || '').trim();
            let nokp = String(row[columnMap['NOKP']] || '').trim();

            // Skip empty rows
            if (!nama && !nokp) continue;

            // Remove leading apostrophe from NoKP (Excel formatting)
            if (nokp.startsWith("'")) {
              nokp = nokp.substring(1);
            }

            const statusWarga = String(row[columnMap['STATUSWARGANEGARA']] || '').trim();
            const tahun = String(row[columnMap['TAHUN']] || '').trim();
            const kelas = String(row[columnMap['KELAS']] || '').trim();
            let jantina = String(row[columnMap['JANTINA']] || '').trim().toUpperCase();

            // Normalize gender (L/P, Male/Female, Lelaki/Perempuan)
            if (jantina === 'L' || jantina === 'MALE' || jantina === 'LELAKI') {
              jantina = 'LELAKI';
            } else if (jantina === 'P' || jantina === 'F' || jantina === 'FEMALE' || jantina === 'PEREMPUAN') {
              jantina = 'PEREMPUAN';
            }

            const bajuPutih = String(row[columnMap['BAJU PUTIH']] || '').trim().toUpperCase();
            const seluarPanjang = String(row[columnMap['SELUAR PANJANG']] || '').trim().toUpperCase();
            const seluarPendek = String(row[columnMap['SELUAR PENDEK']] || '').trim().toUpperCase();
            const bajuKurung = String(row[columnMap['BAJU KURUNG']] || '').trim().toUpperCase();
            const kainKurung = String(row[columnMap['KAIN KURUNG']] || '').trim().toUpperCase();
            const pinafore = String(row[columnMap['PINAFORE']] || '').trim().toUpperCase();
            const kasut = String(row[columnMap['KASUT']] || '').trim();

            const student = {
              name: nama,
              noKP: nokp,
              citizenStatus: statusWarga || 'WARGANEGARA',
              tahun: tahun,
              class: kelas,
              jantina: jantina,
              bajuPutih: bajuPutih,
              seluarPanjang: seluarPanjang,
              seluarPendek: seluarPendek,
              bajuKurung: bajuKurung,
              kainKurung: kainKurung,
              pinafore: pinafore,
              kasut: kasut,
              sourceRow: i + 1,
            };

            students.push(student);
          }

          console.log(`ImportModal: Parsed ${students.length} students from Excel`);
          resolve({ students, worksheets: workbook.SheetNames });

        } catch (err) {
          console.error('ImportModal: Excel parse error:', err);
          reject(err);
        }
      };

      reader.onerror = (error) => {
        console.error('ImportModal: File read error:', error);
        reject(new Error('Failed to read Excel file'));
      };

      reader.readAsArrayBuffer(file);
    });
  };

  // Handle worksheet selection change
  const handleWorksheetChange = async (e) => {
    const worksheetName = e.target.value;
    setSelectedWorksheet(worksheetName);
    setError(null);
    setIsParsing(true);

    try {
      // Re-parse Excel with selected worksheet
      const result = await parseExcelFile(selectedFile, worksheetName);
      setParsedExcelData(result.students);
      console.log(`ImportModal: Switched to worksheet "${worksheetName}", ${result.students.length} students found`);
    } catch (err) {
      console.error('ImportModal: Error re-parsing worksheet:', err);
      setError(err.message || 'Failed to parse selected worksheet');
    } finally {
      setIsParsing(false);
    }
  };

  // Handle Import/Parse button click
  const handleImport = async () => {
    setError(null);
    setIsParsing(true);

    try {
      let parsedData = null;

      if (activeTab === 'paste') {
        // Parse tab-delimited data locally
        if (!pasteData.trim()) {
          throw new Error(t('studentUniform:importModal.errors.pasteEmpty'));
        }

        const students = parseTabDelimitedData(pasteData);

        // Send to backend for validation
        parsedData = {
          method: 'paste',
          students: students,
        };
      } else if (activeTab === 'upload') {
        // Use pre-parsed Excel data
        if (!selectedFile) {
          throw new Error(t('studentUniform:importModal.errors.fileNotSelected'));
        }

        if (!parsedExcelData || parsedExcelData.length === 0) {
          throw new Error(t('studentUniform:importModal.errors.noDataInFile'));
        }

        // Send parsed students (same format as paste method)
        parsedData = {
          method: 'paste', // Use 'paste' method for backend validation
          students: parsedExcelData,
        };
      }

      // Send to parent component for backend validation
      // Parent will call API and show preview modal
      await onDataParsed(parsedData);

      // Clear form after successful parse
      setPasteData('');
      setSelectedFile(null);
      setWorksheets([]);
      setSelectedWorksheet('');
      setParsedExcelData(null);

    } catch (err) {
      console.error('ImportModal: Parse error:', err);
      setError(err.message || t('studentUniform:importModal.errors.failedToParse'));
    } finally {
      setIsParsing(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div className="import-overlay" onClick={onClose}></div>

      {/* Modal */}
      <div className="import-modal">
        <div className="import-modal-header">
          <h3 title={t('studentUniform:importModal.title', { lng: 'en' })}>{t('studentUniform:importModal.title')}</h3>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div className="import-tabs">
          <button
            className={`tab-button ${activeTab === 'paste' ? 'active' : ''}`}
            onClick={() => setActiveTab('paste')}
            title={t('studentUniform:importModal.tabs.paste', { lng: 'en' })}
          >
            {t('studentUniform:importModal.tabs.paste')}
          </button>
          <button
            className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
            title={t('studentUniform:importModal.tabs.upload', { lng: 'en' })}
          >
            {t('studentUniform:importModal.tabs.upload')}
          </button>
        </div>

        {/* Tab Content */}
        <div className="import-modal-body">
          {/* PASTE TEXT TAB - Old Design */}
          {activeTab === 'paste' && (
            <div className="tab-content paste-tab-old-style">
              <h4 className="paste-title" title={t('studentUniform:importModal.paste.title', { lng: 'en' })}>{t('studentUniform:importModal.paste.title')}</h4>

              <textarea
                className="paste-textarea-old-style"
                placeholder={t('studentUniform:importModal.paste.placeholder')}
                value={pasteData}
                onChange={handlePasteChange}
              />

              {/* Error Display */}
              {error && (
                <div className="error-message">
                  <strong>⚠️ {t('common:error')}:</strong> {error}
                </div>
              )}

              {/* Example Section */}
              <h4 className="example-title" title={t('studentUniform:importModal.paste.exampleTitle', { lng: 'en' })}>{t('studentUniform:importModal.paste.exampleTitle')}</h4>
              <div className="example-table-container">
                <table className="example-table">
                  <thead>
                    <tr>
                      <th>NAMA</th>
                      <th>NOKP</th>
                      <th>STATUSWARGANEGARA</th>
                      <th>TAHUN</th>
                      <th>KELAS</th>
                      <th>JANTINA</th>
                      <th>BAJU PUTIH</th>
                      <th>SELUAR PANJANG</th>
                      <th>SELUAR PENDEK</th>
                      <th>BAJU KURUNG</th>
                      <th>KAIN KURUNG</th>
                      <th>PINAFORE</th>
                      <th>KASUT</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>AHMAD RAZIQ BIN ABD SAHIR</td>
                      <td>160111120786</td>
                      <td>WARGANEGARA</td>
                      <td>TAHUN 2</td>
                      <td>2 CRYSTAL</td>
                      <td>PEREMPUAN</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td>S</td>
                      <td></td>
                      <td>S</td>
                      <td>5</td>
                    </tr>
                    <tr>
                      <td>AINUL MARDHIAH BTE MUSAH</td>
                      <td>160202120786</td>
                      <td>WARGANEGARA</td>
                      <td>TAHUN 2</td>
                      <td>2 CRYSTAL</td>
                      <td>PEREMPUAN</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td>S</td>
                      <td>S</td>
                      <td></td>
                      <td>5</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* UPLOAD EXCEL TAB */}
          {activeTab === 'upload' && (
            <div className="tab-content">
              <div className="instructions">
                <h4 title={t('studentUniform:importModal.upload.title', { lng: 'en' })}>{t('studentUniform:importModal.upload.title')}</h4>
                <p>{t('studentUniform:importModal.upload.description')}</p>
                <p><strong>{t('studentUniform:importModal.upload.requiredColumns')}</strong></p>
                <ul>
                  <li>{t('studentUniform:importModal.upload.minimum')}</li>
                  <li>{t('studentUniform:importModal.upload.optional')}</li>
                  <li>{t('studentUniform:importModal.upload.flexible')}</li>
                </ul>
              </div>

              <div className="file-upload-area">
                <input
                  type="file"
                  id="excel-file-input"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="excel-file-input" className="file-upload-button" title={t('studentUniform:importModal.upload.chooseFile', { lng: 'en' })}>
                  {t('studentUniform:importModal.upload.chooseFile')}
                </label>

                {selectedFile && (
                  <div className="selected-file">
                    ✅ <strong>{selectedFile.name}</strong>
                    ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </div>
                )}
              </div>

              {/* Worksheet Selection (if multiple sheets) */}
              {worksheets.length > 1 && (
                <div style={{ marginTop: '20px' }}>
                  <label htmlFor="worksheet-select" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }} title={t('studentUniform:importModal.upload.selectWorksheet', { lng: 'en' })}>
                    {t('studentUniform:importModal.upload.selectWorksheet')}
                  </label>
                  <select
                    id="worksheet-select"
                    value={selectedWorksheet}
                    onChange={handleWorksheetChange}
                    disabled={isParsing}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    {worksheets.map(sheet => (
                      <option key={sheet} value={sheet}>
                        {sheet}
                      </option>
                    ))}
                  </select>
                  <p style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                    {t('studentUniform:importModal.upload.foundStudents', { count: parsedExcelData?.length || 0, worksheet: selectedWorksheet })}
                  </p>
                </div>
              )}

              {/* Single worksheet info */}
              {worksheets.length === 1 && parsedExcelData && (
                <div style={{ marginTop: '15px', padding: '12px', background: '#e8f5e9', borderRadius: '4px', color: '#2e7d32' }}>
                  ✅ {t('studentUniform:importModal.upload.foundStudents', { count: parsedExcelData.length, worksheet: worksheets[0] })}
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="error-message">
                  <strong>❌ {t('common:error')}:</strong> {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="import-modal-footer">
          <button className="btn-cancel" onClick={onClose} title={activeTab === 'paste' ? t('studentUniform:importModal.close', { lng: 'en' }) : t('studentUniform:importModal.cancel', { lng: 'en' })}>
            {activeTab === 'paste' ? t('studentUniform:importModal.close') : t('studentUniform:importModal.cancel')}
          </button>
          <button
            className="btn-parse"
            onClick={handleImport}
            disabled={isParsing || (activeTab === 'upload' && !parsedExcelData)}
            title={isParsing ? t('studentUniform:importModal.processing', { lng: 'en' }) : ''}
          >
            {isParsing
              ? t('studentUniform:importModal.processing')
              : activeTab === 'paste'
                ? t('studentUniform:importModal.paste.importButton')
                : parsedExcelData
                  ? t('studentUniform:importModal.upload.importButton', { count: parsedExcelData.length })
                  : t('studentUniform:importModal.upload.chooseFileButton')}
          </button>
        </div>
      </div>
    </>
  );
};

export default ImportModal;
